/**
 * Tiptap Rich Text Editor for Comments
 * Minimal setup with bold and bullet points only
 */

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import { Markdown } from '@tiptap/markdown';

/**
 * Initialize a Tiptap editor instance
 * @param {string} elementId - The ID of the element to mount the editor to
 * @param {string} hiddenInputId - The ID of the hidden input to store HTML content
 * @returns {Editor} The Tiptap editor instance
 */
export function initTiptapEditor(elementId, hiddenInputId) {
  const element = document.getElementById(elementId);
  const hiddenInput = document.getElementById(hiddenInputId);
  
  if (!element || !hiddenInput) {
    console.error('Tiptap: Element or hidden input not found', { elementId, hiddenInputId });
    return null;
  }

  const placeholder = element.getAttribute('data-placeholder') || 'Start typing...';

  const editor = new Editor({
    element: element,
    extensions: [
      StarterKit.configure({
        // Disable list extensions from StarterKit since we're adding them separately
        bulletList: false,
        orderedList: false,
        listItem: false,
        // Keep what we need
        bold: true,
        italic: true,
        paragraph: true,
        text: true,
        history: true,
        hardBreak: true,
        dropcursor: true,
        gapcursor: true,
        // Disable features we don't need
        heading: false,
        horizontalRule: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        strike: false,
      }),
      // Add list and formatting extensions separately for Tiptap v3
      BulletList,
      OrderedList,
      ListItem,
      Underline,
      // Add Markdown support
      Markdown,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-content prose prose-sm max-w-none focus:outline-none min-h-[80px] p-3',
        'data-placeholder': placeholder,
      },
    },
    // Update hidden input with Markdown on content change
    onUpdate: ({ editor }) => {
      hiddenInput.value = editor.getMarkdown();
    },
  });

  // Store editor instance on the element for later access
  element.tiptapEditor = editor;
  
  // Setup toolbar buttons
  setupToolbarButtons(elementId, editor);

  return editor;
}

/**
 * Setup toolbar button click handlers
 * @param {string} editorId - The ID of the editor element
 * @param {Editor} editor - The Tiptap editor instance
 */
function setupToolbarButtons(editorId, editor) {
  const toolbarButtons = document.querySelectorAll(`[data-editor="${editorId}"]`);
  
  toolbarButtons.forEach(button => {
    const action = button.getAttribute('data-action');
    
    button.addEventListener('click', (e) => {
      e.preventDefault();
      
      switch(action) {
        case 'bold':
          editor.chain().focus().toggleBold().run();
          break;
        case 'italic':
          editor.chain().focus().toggleItalic().run();
          break;
        case 'underline':
          editor.chain().focus().toggleUnderline().run();
          break;
        case 'bulletList':
          editor.chain().focus().toggleBulletList().run();
          break;
      }
      
      // Update button active state
      updateToolbarButtonStates(editorId, editor);
    });
  });
  
  // Update button states on selection change
  editor.on('selectionUpdate', () => {
    updateToolbarButtonStates(editorId, editor);
  });
  
  // Initial button state update
  updateToolbarButtonStates(editorId, editor);
}

/**
 * Update toolbar button active states based on current editor state
 * @param {string} editorId - The ID of the editor element
 * @param {Editor} editor - The Tiptap editor instance
 */
function updateToolbarButtonStates(editorId, editor) {
  const toolbarButtons = document.querySelectorAll(`[data-editor="${editorId}"]`);
  
  toolbarButtons.forEach(button => {
    const action = button.getAttribute('data-action');
    let isActive = false;
    
    switch(action) {
      case 'bold':
        isActive = editor.isActive('bold');
        break;
      case 'italic':
        isActive = editor.isActive('italic');
        break;
      case 'underline':
        isActive = editor.isActive('underline');
        break;
      case 'bulletList':
        isActive = editor.isActive('bulletList');
        break;
    }
    
    if (isActive) {
      button.classList.add('is-active');
    } else {
      button.classList.remove('is-active');
    }
  });
}

/**
 * Initialize Tiptap editors for all comment forms on the page
 * Called automatically via HTMX after content swap
 */
export function initAllCommentEditors() {
  const editorContainers = document.querySelectorAll('[data-tiptap-editor]');
  
  editorContainers.forEach(container => {
    const editorId = container.id;
    const hiddenInputId = container.getAttribute('data-hidden-input');
    
    // Don't reinitialize if already exists
    if (container.tiptapEditor) {
      return;
    }
    
    initTiptapEditor(editorId, hiddenInputId);
  });
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initAllCommentEditors();
});

// Re-initialize after HTMX content swaps
document.body.addEventListener('htmx:afterSwap', () => {
  initAllCommentEditors();
});

